import React, { useCallback, useRef } from 'react';
import { TableRow, Theme, useTheme } from '@mui/material';
import { SxProps } from '@mui/material';
import {
    isInteractiveTarget,
    useTouchDoubleActivation,
} from '../../../../helpers';

interface ICustomTableBodyRowProps {
    children: React.ReactNode;
    onContextMenu?: (e: React.MouseEvent) => void;
    alternate?: boolean;
    alternateColor?: string;
    baseColor?: string;
    sx?: SxProps<Theme>;
    onClick?: () => void;
    onDoubleClick?: () => void;
}

export function DTableBodyRow({
    children,
    onContextMenu,
    alternate = false,
    alternateColor: alternateColorProp,
    baseColor,
    sx,
    onClick,
    onDoubleClick,
}: ICustomTableBodyRowProps) {
    const theme = useTheme();
    const alternateColor =
        alternateColorProp ??
        (theme.palette.mode === 'dark' ? '#303030' : '#E0E0E0');
    const hoverColor = theme.palette.mode === 'dark' ? '#4a4a4a' : '#BDBDBD';
    const rowRef = useRef<HTMLTableRowElement>(null);
    const handleTouchEnd = useTouchDoubleActivation(onDoubleClick);

    const handleDoubleClick = useCallback(
        (e: React.MouseEvent<HTMLTableRowElement>) => {
            if (!onDoubleClick) return;
            if (rowRef.current && isInteractiveTarget(e, rowRef.current))
                return;
            onDoubleClick();
        },
        [onDoubleClick],
    );

    const handleClick = useCallback(
        (e: React.MouseEvent<HTMLTableRowElement>) => {
            if (!onClick) return;
            if (rowRef.current && isInteractiveTarget(e, rowRef.current))
                return;
            onClick();
        },
        [onClick],
    );

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLTableRowElement>) => {
            const activate = onDoubleClick ?? onClick;
            if (!activate) return;
            if (e.key !== 'Enter') return;
            if (rowRef.current && isInteractiveTarget(e, rowRef.current))
                return;
            e.preventDefault();
            activate();
        },
        [onClick, onDoubleClick],
    );

    return (
        <TableRow
            ref={rowRef}
            onClick={onClick ? handleClick : undefined}
            onDoubleClick={onDoubleClick ? handleDoubleClick : undefined}
            onTouchEnd={onDoubleClick ? handleTouchEnd : undefined}
            onKeyDown={onDoubleClick || onClick ? handleKeyDown : undefined}
            tabIndex={onDoubleClick || onClick ? 0 : undefined}
            title={
                onDoubleClick
                    ? 'Doble clic o doble toque para abrir'
                    : onClick
                    ? 'Clic, toque o Enter para seleccionar'
                    : undefined
            }
            sx={{
                fontWeight: 'regular',
                backgroundColor: alternate ? alternateColor : baseColor,
                cursor: onDoubleClick || onClick ? 'pointer' : undefined,
                touchAction:
                    onDoubleClick || onClick ? 'manipulation' : undefined,
                ...(onDoubleClick || onClick
                    ? {
                          '&:hover': { backgroundColor: hoverColor },
                          '&:focus-visible': {
                              outline: '2px solid',
                              outlineColor: 'primary.main',
                              outlineOffset: -2,
                          },
                      }
                    : {}),
                ...sx,
            }}
            onContextMenu={onContextMenu}
        >
            {children}
        </TableRow>
    );
}
